// ── PC 렌더 경로 (>768px) ─────────────────────────────────────────────────────
// 헤더 요약 + 마케터 칩 선택 바 + 3패널 (좌: 매장카드, 중: 날짜순, 우: 정산입금)

import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { C } from './constants';
import { PAGE_WRAP, CHIP_BAR, PANELS } from './pcStyles';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from '../adminlayout/designStandard';
import { PcLeftPanel }   from './PcLeftPanel';
import { PcCenterPanel } from './PcCenterPanel';
import { PcRightPanel }  from './PcRightPanel';
import { SummaryHeader } from '../../../components/settlement/SummaryHeader';

export function PcView({
  // 마케터 선택
  marketers, selectedUser, setSelectedUser,
  // 마케터 숨기기 (정산예정금액 위젯 + 이 바에서 제외)
  showHiddenMarketers, setShowHiddenMarketers, isMarketerHidden, toggleMarketerHidden,
  // 요약 수치
  summary, recTotal, netExpected,
  // 상담자 배분율 정보 (없으면 null)
  consultantInfo,
  // 좌 패널
  marketerData, dataLoading, unmTotal, leftItems,
  leftTab, setLeftTab,
  selectedPay, setSelectedPay, handlePaymentClick, handleStoreCardClick, handleUnlink, linking,
  expandedId, setExpandedId,
  onEditShareRate,         // 상담자 배분율 편집 핸들러
  // 중 패널
  // 우 패널
  records, recLoading, editId, formDate, setFormDate,
  formAmt, formNote, setFormNote, saving,
  handleSave, resetForm, selectRecord, handleDelete, onAmtChange,
}) {
  return (
    <div style={PAGE_WRAP}>

      {/* ── 헤더: 표준 텍스트 헤더(카드 박스 제거) + 순수익 요약 ── */}
      <div style={{ flexShrink: 0 }}>
        <div style={stdHeaderRow(false)}>
          <div>
            <h2 style={stdPageTitle}>
              정산서 관리
              {selectedUser && (
                <span style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, marginLeft: 10 }}>— {selectedUser.name}</span>
              )}
            </h2>
            <div style={stdPageSubtitle}>마케터 정산서를 관리합니다.</div>
          </div>
        </div>
        {selectedUser && (
          <div style={{ marginBottom: 12 }}>
            <SummaryHeader
              totalMinus={summary.totalMinus}
              totalPlus={summary.totalPlus}
              recTotal={recTotal}
              netExpected={netExpected}
              variant="row-divider"
            />
          </div>
        )}
      </div>

      {/* ── 본문 카드: 칩 바 + 3패널 (1px 테두리·라운드 8·그림자 없음 — 디자인 표준) ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-card)' }}>

      {/* ── 마케터 칩 선택 바 ── */}
      <div style={CHIP_BAR}>
        {(() => {
          const hiddenCount = marketers.filter((mk) => isMarketerHidden?.(mk)).length;
          const visible = marketers.filter((mk) => showHiddenMarketers || !isMarketerHidden?.(mk));
          if (marketers.length === 0) {
            return <span style={{ fontSize: 12, color: C.sub }}>정산서 배정된 마케터가 없습니다.</span>;
          }
          return (
            <>
              {visible.map((mk) => {
                const active = selectedUser?.id === mk.id;
                const hidden = isMarketerHidden?.(mk);
                return (
                  <span
                    key={mk.id}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 6px 3px 12px', borderRadius: 20,
                      border: active ? '2px solid #2563eb' : `1px solid ${C.border}`,
                      background: active ? 'rgba(37,99,235,0.10)' : 'var(--bg-card)',
                      opacity: hidden ? 0.5 : 1, transition: 'all 0.15s',
                    }}
                  >
                    <button
                      onClick={() => setSelectedUser(mk)}
                      style={{
                        fontSize: 12, fontWeight: active ? 700 : 500,
                        border: 'none', background: 'none', padding: 0,
                        color: active ? '#2563eb' : C.main, cursor: 'pointer',
                      }}
                    >
                      {mk.name}{hidden ? ' (숨김)' : ''}
                    </button>
                    <button
                      onClick={() => toggleMarketerHidden?.(mk, !hidden)}
                      title={hidden ? '위젯에 다시 표시' : '정산예정금액 위젯에서 숨기기'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        border: 'none', background: 'none', padding: 2, cursor: 'pointer',
                        color: hidden ? '#2563eb' : C.sub,
                      }}
                    >
                      {hidden ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                  </span>
                );
              })}
              {/* 숨긴마케터 같이보기 토글 */}
              {(hiddenCount > 0 || showHiddenMarketers) && (
                <button
                  onClick={() => setShowHiddenMarketers?.((v) => !v)}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 11px', borderRadius: 20, marginLeft: 'auto',
                    border: `1px dashed ${C.border}`,
                    background: showHiddenMarketers ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)',
                    color: showHiddenMarketers ? '#2563eb' : C.sub, cursor: 'pointer',
                  }}
                >
                  {showHiddenMarketers ? '숨긴마케터 숨기기' : `숨긴마케터 같이보기${hiddenCount ? ` (${hiddenCount})` : ''}`}
                </button>
              )}
            </>
          );
        })()}
      </div>

      {/* ── 3패널 ── */}
      <div style={PANELS}>
        <PcLeftPanel
          selectedUser={selectedUser}
          dataLoading={dataLoading}
          marketerData={marketerData}
          unmTotal={unmTotal}
          leftItems={leftItems}
          leftTab={leftTab}
          setLeftTab={setLeftTab}
          selectedPay={selectedPay}
          setSelectedPay={setSelectedPay}
          handlePaymentClick={handlePaymentClick}
          handleStoreCardClick={handleStoreCardClick}
          handleUnlink={handleUnlink}
          linking={linking}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
          onEditShareRate={onEditShareRate}
        />
        <PcCenterPanel
          selectedUser={selectedUser}
          dataLoading={dataLoading}
          marketerData={marketerData}
          records={records}
          unmTotal={unmTotal}
        />
        <PcRightPanel
          selectedUser={selectedUser}
          recLoading={recLoading}
          records={records}
          recTotal={recTotal}
          editId={editId}
          formDate={formDate}
          setFormDate={setFormDate}
          formAmt={formAmt}
          formNote={formNote}
          setFormNote={setFormNote}
          saving={saving}
          handleSave={handleSave}
          resetForm={resetForm}
          selectRecord={selectRecord}
          handleDelete={handleDelete}
          onAmtChange={onAmtChange}
        />
      </div>

      </div>{/* ── 본문 카드 끝 ── */}
    </div>
  );
}
